/**
 * SocialPulseSimple — thin editorial page used for stubbed / in-progress
 * parent-domain routes (/press, /careers, /vision-mission, /privacy,
 * /community-guidelines, and the generic parent-domain 404 fallback).
 *
 * Every legacy chrome/route on socialpulse.com is being retired in
 * favour of the modern SocialPulseHeader + Footer chrome. Rather than
 * leaving a bunch of "route not found → legacy 404" cracks, we render
 * a clean editorial "In progress" card so visitors and crawlers never
 * see the old design again.
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Mail } from "lucide-react";
import SocialPulseHeader from "@/components/socialpulse/SocialPulseHeader";
import SocialPulseFooter from "@/components/socialpulse/SocialPulseFooter";

function setMeta(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}

export default function SocialPulseSimple({
  eyebrow = "SocialPulse",
  title = "In progress",
  subtitle = "This page is being rebuilt in the new SocialPulse chrome.",
  body = null,
  testid = "sp-simple-page",
  primaryCta = { to: "/", label: "Back to home" },
  secondaryCta = { to: "/network", label: "Browse the network" },
  robots = "noindex, follow",
}) {
  useEffect(() => {
    document.title = `${title} — SocialPulse`;
    setMeta("robots", robots);
  }, [title, robots]);

  return (
    <div className="min-h-screen bg-white" data-testid={testid}>
      <SocialPulseHeader />

      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="absolute -top-24 -left-20 w-96 h-96 rounded-full blur-3xl opacity-40 pointer-events-none"
          style={{ background: "radial-gradient(circle,#A7F3D0 0%,transparent 70%)" }}
        />
        <div
          aria-hidden
          className="absolute top-8 right-0 w-80 h-80 rounded-full blur-3xl opacity-40 pointer-events-none"
          style={{ background: "radial-gradient(circle,#DDD6FE 0%,transparent 75%)" }}
        />

        <div className="relative max-w-[900px] mx-auto px-5 sm:px-6 lg:px-10 py-16 sm:py-24">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[.22em] text-violet-700">
            <Sparkles className="w-3.5 h-3.5" /> {eyebrow}
          </div>
          <h1
            data-testid={`${testid}-title`}
            className="mt-3 font-serif font-bold tracking-tight text-slate-900 leading-[1.05] text-3xl sm:text-4xl lg:text-5xl"
          >
            {title}
          </h1>
          {subtitle && (
            <p className="mt-4 text-slate-600 text-base sm:text-lg leading-relaxed max-w-2xl">
              {subtitle}
            </p>
          )}
          {body && (
            <div className="mt-6 prose prose-slate max-w-2xl text-slate-700 leading-relaxed">
              {body}
            </div>
          )}

          <div className="mt-8 flex flex-wrap gap-3">
            {primaryCta && (
              <Link
                to={primaryCta.to}
                data-testid={`${testid}-primary-cta`}
                className="inline-flex items-center gap-2 rounded-full bg-violet-600 hover:bg-violet-700 text-white font-semibold px-5 py-2.5 text-sm transition-colors shadow-sm"
              >
                {primaryCta.label} <ArrowRight className="w-4 h-4" />
              </Link>
            )}
            {secondaryCta && (
              <Link
                to={secondaryCta.to}
                data-testid={`${testid}-secondary-cta`}
                className="inline-flex items-center gap-2 rounded-full ring-1 ring-slate-300 hover:ring-violet-400 text-slate-700 hover:text-violet-700 font-semibold px-5 py-2.5 text-sm transition-colors bg-white/70 backdrop-blur"
              >
                {secondaryCta.label}
              </Link>
            )}
            <Link
              to="/contact"
              data-testid={`${testid}-contact-cta`}
              className="inline-flex items-center gap-2 rounded-full ring-1 ring-slate-300 hover:ring-emerald-400 text-slate-700 hover:text-emerald-700 font-semibold px-5 py-2.5 text-sm transition-colors bg-white/70 backdrop-blur"
            >
              <Mail className="w-4 h-4" /> Contact us
            </Link>
          </div>
        </div>
      </section>

      <SocialPulseFooter />
    </div>
  );
}

/* ─── Ready-made pages that only pass copy into the shell ─────────── */

export const SocialPulsePress = () => (
  <SocialPulseSimple
    eyebrow="Press"
    title="Press & media"
    subtitle="Journalists and researchers — reach out via the contact form for interviews, deck requests, or press images."
    testid="sp-press-page"
    robots="index, follow"
  />
);

export const SocialPulseCareers = () => (
  <SocialPulseSimple
    eyebrow="Careers"
    title="Build the network with us"
    subtitle="We hire small, remote-first teams across engineering, community, and content. Introduce yourself — no formal openings required."
    testid="sp-careers-page"
    robots="index, follow"
  />
);

export const SocialPulseVisionMission = () => (
  <SocialPulseSimple
    eyebrow="Vision & Mission"
    title="Different places. Brighter people."
    subtitle="Every community deserves its own pulse. SocialPulse is the identity, discovery and civic layer that ties them together."
    testid="sp-vision-page"
    robots="index, follow"
  />
);

export const SocialPulsePrivacy = () => (
  <SocialPulseSimple
    eyebrow="Privacy"
    title="Privacy Policy"
    subtitle="Your data belongs to you. This page will be replaced with the full policy shortly — for the interim, contact us with any privacy questions."
    testid="sp-privacy-page"
    robots="index, follow"
  />
);

export const SocialPulseCommunityGuidelines = () => (
  <SocialPulseSimple
    eyebrow="Community"
    title="Community Guidelines"
    subtitle="Kindness · Accuracy · Locality. The full guidelines are coming — for now, we moderate against those three principles."
    testid="sp-guidelines-page"
    robots="index, follow"
  />
);

export const SocialPulseTopics = () => (
  <SocialPulseSimple
    eyebrow="Topics"
    title="Topics on the network"
    subtitle="Every topic is owned by a specialist portal — visit the Network page to jump straight to the right pulse."
    testid="sp-topics-page"
    primaryCta={{ to: "/network", label: "Browse the network" }}
    secondaryCta={{ to: "/places", label: "Explore places" }}
    robots="index, follow"
  />
);

export const SocialPulseNotFound = () => (
  <SocialPulseSimple
    eyebrow="404"
    title="This page doesn't live here."
    subtitle="SocialPulse.com is the network router — the page you were looking for may live on a sister site."
    testid="sp-404-page"
    primaryCta={{ to: "/", label: "Back to home" }}
    secondaryCta={{ to: "/search", label: "Search the network" }}
    robots="noindex, follow"
  />
);
